import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBack } from '../hooks';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { C, cs } from '../styles/common';
import api from '../api';
import LastChanged, { HistoryLink } from '../components/LastChanged';
import { filled } from '../numberFormat';

function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error(`Cannot load ${file.name} — try a JPG or PNG`)); };
    img.onload = () => {
      const MAX = 1200;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width >= height) { height = Math.round((height / width) * MAX); width = MAX; }
        else                 { width  = Math.round((width / height) * MAX); height = MAX; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      canvas.toBlob(blob => {
        if (!blob) { reject(new Error(`Failed to compress ${file.name}`)); return; }
        resolve(blob);
      }, 'image/jpeg', 0.78);
    };
    img.src = url;
  });
}

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function uploadPhoto(file, machineId) {
  const ext  = file.name.split('.').pop();
  const path = `machine-${machineId}-${Date.now()}.${ext}`;
  const res  = await fetch(
    `${SUPABASE_URL}/storage/v1/object/machine-photos/${path}`,
    { method: 'POST', headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` }, body: file }
  );
  if (!res.ok) throw new Error('Upload failed');
  return `${SUPABASE_URL}/storage/v1/object/public/machine-photos/${path}`;
}

// Warm-up time is subtracted from every shift before capacity is worked out, so a
// machine without one is planned against a guess. Mirrors DEFAULT_WARMUP_MIN in
// backend/src/routes/orders.js — an unset value there falls back to this, silently.
const DEFAULT_WARMUP_MIN = 30;
const assumedWarmup = m => m?.warmup_min == null && m?.status !== 'archived';

// ── MACHINES LIST ─────────────────────────────────────────────
export default function Machines() {
  const navigate = useNavigate();
  const goBack   = useBack('/admin');
  const { isDirector } = useAuth();
  const [machines, setMachines]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [selected, setSelected]             = useState(null);
  const [editing, setEditing]               = useState(null);
  const [showDecommissioned, setShowDecommissioned] = useState(false);
  const [maintenanceModal, setMaintenanceModal]     = useState(null);

  function load() {
    api.get('/api/v1/machines')
      .then(res => { setMachines(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  function handleMaintenanceAffected(machine, affectedJobs) {
    setMaintenanceModal({ machine, affectedJobs });
  }

  if (loading) return <div style={s.center}>Loading machines...</div>;

  if (editing !== null) return (
    <>
      <MachineForm
        machine={editing === 'new' ? null : editing}
        isDirector={isDirector()}
        onSaved={() => { setEditing(null); setSelected(null); load(); }}
        onCancel={() => setEditing(null)}
        onMaintenanceAffected={handleMaintenanceAffected}
      />
      {maintenanceModal && (
        <MachineMaintenanceModal
          machine={maintenanceModal.machine}
          affectedJobs={maintenanceModal.affectedJobs}
          onDone={() => { setMaintenanceModal(null); load(); }}
        />
      )}
    </>
  );

  if (selected) return (
    <>
      <MachineDetail
        machine={selected}
        isDirector={isDirector()}
        onEdit={() => setEditing(selected)}
        onBack={() => setSelected(null)}
        onDeleted={() => { setSelected(null); load(); }}
        onUpdated={updated => { setSelected(updated); load(); }}
        onMaintenanceAffected={handleMaintenanceAffected}
      />
      {maintenanceModal && (
        <MachineMaintenanceModal
          machine={maintenanceModal.machine}
          affectedJobs={maintenanceModal.affectedJobs}
          onDone={() => { setMaintenanceModal(null); load(); }}
        />
      )}
    </>
  );

  return (
    <div style={s.page}>
      <div style={{ padding: '8px 0 0', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <button onClick={goBack} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '12px', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
        </button>
        <span style={{ color: '#2a3040' }}>·</span>
        <span style={{ fontSize: '12px', color: '#f0f2f5' }}>Machines</span>
      </div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Machines</h1>
          <p style={s.sub}>
            {machines.filter(m => m.status !== 'archived').length} active · {machines.filter(m => m.status === 'archived').length} decommissioned
            {machines.filter(assumedWarmup).length > 0 && (
              <>
                {' · '}
                <span style={{ color: '#f59e0b', fontWeight: '600' }}
                  title={`Scheduling assumes ${DEFAULT_WARMUP_MIN} min of warm-up a day on these machines, which is roughly 6% of a shift`}>
                  {machines.filter(assumedWarmup).length} using assumed warm-up
                </span>
              </>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={() => setShowDecommissioned(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px',
              background: showDecommissioned ? '#1f2937' : 'none',
              color: showDecommissioned ? '#9ca3af' : '#6b7280',
              border: `1px solid ${showDecommissioned ? '#4b5563' : '#2a3040'}`,
              borderRadius: '6px', padding: '6px 12px', fontSize: '11px',
              cursor: 'pointer', fontWeight: '600', fontFamily: 'system-ui, sans-serif' }}>
            <i className={`fa-solid fa-eye${showDecommissioned ? '' : '-slash'}`} style={{ fontSize: '10px' }} />
            {showDecommissioned ? 'Showing decommissioned' : 'Decommissioned hidden'}
          </button>
          {isDirector() && (
            <>
              <HistoryLink type="machine" />
              <button style={s.importBtn} onClick={() => navigate('/admin/machines/import')}>
                <i className="fa-solid fa-file-import" style={{ marginRight: '6px' }} />Import
              </button>
              <button style={s.addBtn} onClick={() => setEditing('new')}>+ Add Machine</button>
            </>
          )}
        </div>
      </div>
      <div style={s.grid}>
        {machines.filter(m => showDecommissioned || m.status !== 'archived').map(machine => (
          <div key={machine.id} style={s.card} onClick={() => setSelected(machine)}>
            <div style={s.cardHeader}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
                <span style={{ ...s.machineName, color: assumedWarmup(machine) ? '#f59e0b' : undefined }}>
                  {machine.name}
                </span>
                {assumedWarmup(machine) && (
                  <span
                    title={`No warm-up time set — scheduling assumes ${DEFAULT_WARMUP_MIN} min/day for this machine`}
                    style={s.warmupFlag}
                  >
                    warm-up?
                  </span>
                )}
              </span>
              <span style={{ ...s.statusDot, background: machine.status === 'available' ? '#22c55e' : machine.status === 'maintenance' ? '#f59e0b' : machine.status === 'archived' ? '#6b7280' : '#ef4444' }} />
            </div>
            <div style={s.cardBody}>
              <div style={s.site}>{machine.site}</div>
              <div style={s.specs}>
                <Spec label="Clamp"     value={`${machine.tonnes}T`} />
                <Spec label="Screw"     value={`⌀${machine.screw_mm}mm`} />
                <Spec label="Barrel"    value={`${machine.barrel_cm3}cm³`} />
                <Spec label="Max inj"   value={`${machine.max_inj_bar}bar`} />
                <Spec label="Screw spd" value={`${machine.screw_spd_min}–${machine.screw_spd_max}rpm`} />
                <Spec label="Back pres" value={`${machine.back_pres_min}–${machine.back_pres_max}bar`} />
              </div>
            </div>
            <div style={s.cardFooter}>
              <span style={{
                ...s.statusChip,
                background: machine.status === 'available' ? '#166534' : machine.status === 'maintenance' ? '#78350f' : machine.status === 'archived' ? '#1f2937' : '#7f1d1d',
                color:      machine.status === 'available' ? '#86efac' : machine.status === 'maintenance' ? '#fde68a' : machine.status === 'archived' ? '#9ca3af' : '#fca5a5',
              }}>
                {machine.status === 'available' ? '● Available' : machine.status === 'maintenance' ? '● Maintenance' : machine.status === 'archived' ? '● Decommissioned' : '● In use'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── MACHINE DETAIL ────────────────────────────────────────────
function MachineDetail({ machine: m, isDirector, onEdit, onBack, onDeleted, onUpdated, onMaintenanceAffected }) {
  const { toast, confirm } = useToast();
  const [deleting, setDeleting]           = useState(false);
  const [deleteError, setDeleteError]     = useState('');
  const [decommissioning, setDecommissioning] = useState(false);
  const [checkingRefs, setCheckingRefs]   = useState(false);
  const [warnRefs, setWarnRefs]           = useState(null);
  const [uploading, setUploading]         = useState(false);
  const [isMobile, setIsMobile]           = useState(window.innerWidth < 640);
  const fileRef = useRef();

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  function deleteMachine() {
    confirm({
      message: `Delete ${m.name}? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: () => {
        setDeleting(true);
        setDeleteError('');
        api.delete(`/api/v1/machines/${m.id}`)
          .then(() => onDeleted())
          .catch(err => {
            setDeleteError(err.response?.data?.error || 'Delete failed — try again');
            setDeleting(false);
          });
      },
    });
  }

  function doDecommission() {
    confirm({
      message: `Decommission ${m.name}? It will no longer appear in scheduling but can be recommissioned later.`,
      confirmLabel: 'Decommission',
      danger: true,
      onConfirm: () => {
        setDecommissioning(true);
        api.put(`/api/v1/machines/${m.id}`, { status: 'archived' })
          .then(res => { onUpdated(res.data); })
          .catch(() => toast.error('Failed to decommission machine'))
          .finally(() => setDecommissioning(false));
      },
    });
  }

  async function decommissionMachine() {
    setCheckingRefs(true);
    try {
      const res = await api.get(`/api/v1/machines/${m.id}/references`);
      const { parts = [], moulds = [] } = res.data;
      if (parts.length > 0 || moulds.length > 0) {
        setWarnRefs({ parts, moulds });
      } else {
        doDecommission();
      }
    } catch {
      doDecommission();
    } finally {
      setCheckingRefs(false);
    }
  }

  function recommissionMachine() {
    confirm({
      message: `Recommission ${m.name}? It will be marked as Available again.`,
      confirmLabel: 'Recommission',
      onConfirm: () => {
        setDecommissioning(true);
        api.put(`/api/v1/machines/${m.id}`, { status: 'available' })
          .then(res => { onUpdated(res.data); })
          .catch(() => toast.error('Failed to recommission machine'))
          .finally(() => setDecommissioning(false));
      },
    });
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadPhoto(file, m.id);
      const res = await api.put(`/api/v1/machines/${m.id}`, { photo_url: url });
      onUpdated(res.data);
    } catch { toast.error('Photo upload failed'); }
    setUploading(false);
  }

  const v = (val, unit = '') => val != null && val !== '' ? `${val}${unit}` : '—';
  const b = val => val ? 'Yes' : 'No';

  return (
    <>
    <div style={s.page}>
      {!isMobile && (
        <div style={s.banner}>
          {m.photo_url
            ? <img src={m.photo_url} alt={m.name} style={s.bannerImg} />
            : <div style={s.bannerPlaceholder}>No photo — click to add</div>}
          {isDirector && (
            <>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
              <button style={s.uploadBtn} onClick={() => fileRef.current.click()} disabled={uploading}>
                {uploading ? 'Uploading...' : m.photo_url ? '📷 Change photo' : '📷 Add photo'}
              </button>
            </>
          )}
        </div>
      )}

      <div style={s.detailHeader}>
        <button style={s.backBtn} onClick={onBack}>← Back</button>
        {isDirector && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
            <div style={s.detailActions}>
              {isMobile && (
                <>
                  <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
                  <button style={s.editBtn} onClick={() => fileRef.current.click()} disabled={uploading}>{uploading ? '...' : '📷'}</button>
                </>
              )}
              <button style={s.editBtn} onClick={onEdit}>Edit</button>
              {m.status === 'archived'
                ? <button style={{ background: '#166534', color: '#86efac', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
                    onClick={recommissionMachine} disabled={decommissioning}>
                    {decommissioning ? 'Updating...' : 'Recommission'}
                  </button>
                : <button style={{ background: '#374151', color: '#9ca3af', border: '1px solid #4b5563', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
                    onClick={decommissionMachine} disabled={decommissioning || checkingRefs}>
                    {checkingRefs ? 'Checking...' : decommissioning ? 'Updating...' : 'Decommission'}
                  </button>
              }
              <button style={s.deleteBtn} onClick={deleteMachine} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
            {deleteError && (
              <div style={{ fontSize: '12px', color: '#fca5a5', maxWidth: '320px', textAlign: 'right' }}>{deleteError}</div>
            )}
          </div>
        )}
      </div>

      <div style={s.titleRow}>
        {isMobile && m.photo_url && <img src={m.photo_url} alt={m.name} style={s.thumbnail} />}
        <div>
          <div style={s.detailTitle}>{m.name}</div>
          <div style={s.detailSub}>{m.maker} · {m.site}</div>
        </div>
      </div>

      {m.machine_notes && (
        <div style={s.notesBox}>
          <div style={s.notesTitle}>ℹ Machine Notes</div>
          <div style={s.notesText}>{m.machine_notes}</div>
        </div>
      )}

      <div style={s.detailGrid}>
        <Section title="Clamping">
          <Row label="Clamp force"         value={`${m.tonnes}T / ${v(m.clamp_kn)}kN`} />
          <Row label="Mould height min"    value={v(m.mould_min_mm, 'mm')} />
          <Row label="Mould height max"    value={v(m.mould_max_mm, 'mm')} />
          <Row label="Stroke"              value={v(m.stroke_mm, 'mm')} />
          <Row label="Tie bar H × V"       value={m.tie_bar_h_mm && m.tie_bar_v_mm ? `${m.tie_bar_h_mm} × ${m.tie_bar_v_mm}mm` : '—'} />
          <Row label="Tie bar diameter"    value={v(m.tie_bar_dia_mm, 'mm')} />
          <Row label="Clamping speed"      value={v(m.clamp_speed_mm_s, 'mm/s')} />
          <Row label="Dry cycle time"      value={v(m.dry_cycle_s, 's')} />
          <Row label="Clamping type"       value={m.clamp_type || '—'} />
        </Section>

        <Section title="Injection">
          <Row label="Screw diameter"      value={v(m.screw_mm, 'mm')} />
          <Row label="Barrel volume"       value={v(m.barrel_cm3, 'cm³')} />
          <Row label="L/D ratio"           value={v(m.ld_ratio)} />
          <Row label="Max inj pressure"    value={v(m.max_inj_bar, 'bar')} />
          <Row label="Max stages"          value={v(m.max_stg)} />
          <Row label="Hold stages"         value={v(m.hld_stg)} />
          <Row label="Max inj speed"       value={v(m.max_inj_speed_mm_s, 'mm/s')} />
          <Row label="Injection stroke"    value={v(m.inj_stroke_mm, 'mm')} />
          <Row label="Screw type"          value={m.screw_type || '—'} />
          <Row label="Nozzle type"         value={m.nozzle_type || '—'} />
          <Row label="Nozzle radius"       value={v(m.nozzle_radius_mm, 'mm')} />
          <Row label="V→P switchover"      value={m.vp_type || '—'} />
          <Row label="Per-stage pressure"  value={b(m.per_stg)} />
        </Section>

        <Section title="Plasticising">
          <Row label="Screw speed"         value={`${v(m.screw_spd_min)} – ${v(m.screw_spd_max)} rpm`} />
          <Row label="Back pressure"       value={`${v(m.back_pres_min)} – ${v(m.back_pres_max)} bar`} />
          <Row label="Max plast. rate"     value={v(m.max_plast_rate_g_s, 'g/s')} />
          <Row label="Screw torque"        value={v(m.torque_nm, 'N·m')} />
        </Section>

        <Section title="Ejection">
          <Row label="Ejector force"       value={v(m.ej_force_kn, 'kN')} />
          <Row label="Ejector stroke"      value={v(m.ej_stroke_mm, 'mm')} />
          <Row label="Ejector speed"       value={v(m.ej_speed_mm_s, 'mm/s')} />
          <Row label="Ejector positions"   value={v(m.ej_positions)} />
          <Row label="Coupling type"       value={m.ej_coupling || '—'} />
          <Row label="Air valve ports"     value={v(m.air_valve_ports)} />
        </Section>

        <Section title="Movement">
          <Row label="Platen speed"        value={v(m.plat_spd_mm_s, 'mm/s')} />
          <Row label="Move stages"         value={v(m.mv_stg)} />
        </Section>

        <Section title="Miscellaneous">
          <Row label="Machine type"            value={m.drive_type || '—'} />
          <Row label="Injection units"         value={v(m.injection_units)} />
          <Row label="Platen type"             value={m.platen_type || '—'} />
          <Row label="Robot / automation"      value={m.robot_type || '—'} />
          <Row label="Nozzle contact force"    value={v(m.nozzle_contact_force_bar, 'bar')} />
          <Row label="Decomp. before dosing"   value={v(m.decomp_before_mm, 'mm')} />
          <Row label="Decomp. after dosing"    value={v(m.decomp_after_mm, 'mm')} />
          <Row label="Mould breathing"         value={b(m.mould_breathing)} />
          <Row label="Magnetic clamping"       value={b(m.magnetic_clamping)} />
        </Section>

        <Section title="Peripheral Equipment">
          <Row label="Mould temp ports"    value={v(m.mould_temp_ports)} />
          <Row label="Conveyor"            value={b(m.conveyor)} />
          <Row label="Granulator"          value={b(m.granulator)} />
          <Row label="Chiller connected"   value={b(m.chiller_connected)} />
          <Row label="Hot runner ctrl"     value={b(m.hot_runner_controller)} />
          {m.hot_runner_controller && <Row label="Hot runner brand" value={m.hot_runner_brand || '—'} />}
        </Section>

        <Section title="Controller">
          <Row label="Brand"               value={m.controller_brand || '—'} />
          <Row label="Model"               value={m.controller_model || '—'} />
          <Row label="Software version"    value={m.software_version || '—'} />
          <Row label="Remote access"       value={b(m.remote_access)} />
        </Section>

        <Section title="Machine info">
          <Row label="Year"                value={v(m.year)} />
          <Row label="Serial no."          value={m.serial_no || '—'} />
          <Row label="Power"               value={v(m.kw, 'kW')} />
          <Row label="Status"              value={m.status} />
        </Section>

        <Section title="Scheduling">
          <Row label="Machine warmup time" value={m.warmup_min != null ? `${m.warmup_min} min` : '30 min (default)'} />
        </Section>

        {isDirector && (
          <Section title="Financial">
            <Row label="Rate per hour"         value={m.rate_per_hr ? `€${m.rate_per_hr}/hr` : '—'} />
            <Row label="Labour rate per hour"  value={m.labour_rate_per_hour ? `€${m.labour_rate_per_hour}/hr` : '—'} />
            <Row label="Maintenance cost/year" value={m.maintenance_cost_year ? `€${m.maintenance_cost_year}` : '—'} />
            <Row label="Electricity rate"      value={m.electricity_rate ? `€${m.electricity_rate}/kWh` : '—'} />
          </Section>
        )}

        <Section title="Safety & Compliance">
          <Row label="Last service"    value={m.last_service_date || '—'} />
          <Row label="Next service due" value={m.next_service_date || '—'} />
          <Row label="CE marked"       value={b(m.ce_marked)} />
        </Section>
      </div>
    </div>

    {warnRefs && (
      <DecommissionWarningModal
        machineId={m.id}
        machineName={m.name}
        parts={warnRefs.parts}
        moulds={warnRefs.moulds}
        onProceed={() => { setWarnRefs(null); doDecommission(); }}
        onClose={() => setWarnRefs(null)}
      />
    )}
    </>
  );
}

function DecommissionWarningModal({ machineId, machineName, parts, moulds, onProceed, onClose }) {
  const navigate = useNavigate();
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#1e2329', border: '1px solid #374151', borderRadius: '12px', padding: '24px', maxWidth: '520px', width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ color: '#f59e0b', fontSize: '16px' }} />
          </div>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#f0f2f5', fontFamily: 'system-ui' }}>References still point to {machineName}</div>
            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px', fontFamily: 'system-ui' }}>
              Decommissioning will not automatically clear these — the scheduler may behave unexpectedly until they are updated.
            </div>
          </div>
        </div>

        <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {parts.length > 0 && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px', fontFamily: 'system-ui' }}>
                Parts ({parts.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {parts.map(p => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#151b22', borderRadius: '6px', padding: '8px 12px', gap: '8px' }}>
                    <div>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#f0f2f5', fontFamily: 'system-ui' }}>{p.name}</span>
                      {p.part_number && <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '6px', fontFamily: 'system-ui' }}>{p.part_number}</span>}
                      <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px', fontFamily: 'system-ui' }}>
                        {[
                          p.primary_machine_id === machineId ? 'Primary machine' : null,
                          (p.secondary_machine_ids || []).includes(machineId) ? 'Secondary machine' : null,
                        ].filter(Boolean).join(' · ') || 'Referenced'}
                      </div>
                    </div>
                    <button
                      onClick={() => { onClose(); navigate(`/admin/parts/${p.id}`); }}
                      style={{ background: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'system-ui' }}>
                      Edit part
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {moulds.length > 0 && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px', fontFamily: 'system-ui' }}>
                Moulds ({moulds.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {moulds.map(mo => (
                  <div key={mo.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#151b22', borderRadius: '6px', padding: '8px 12px', gap: '8px' }}>
                    <div>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#f0f2f5', fontFamily: 'system-ui' }}>{mo.ref}</span>
                      {mo.name && <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '6px', fontFamily: 'system-ui' }}>{mo.name}</span>}
                      <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px', fontFamily: 'system-ui' }}>Preferred machine</div>
                    </div>
                    <button
                      onClick={() => { onClose(); navigate('/admin/moulds'); }}
                      style={{ background: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'system-ui' }}>
                      Go to Moulds
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '4px', borderTop: '1px solid #2a3040' }}>
          <button onClick={onClose} style={{ background: 'transparent', color: '#9ca3af', border: '1px solid #374151', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'system-ui' }}>
            Cancel
          </button>
          <button onClick={onProceed} style={{ background: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.35)', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'system-ui' }}>
            Decommission anyway
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PHOTO EXTRACTOR ───────────────────────────────────────────
function PhotoExtractor({ onExtracted }) {
  const [files, setFiles]         = useState([]);
  const [previews, setPreviews]   = useState([]);
  const [scanning, setScanning]   = useState(false);
  const [scanError, setScanError] = useState('');
  const inputRef = useRef();

  function addFiles(incoming) {
    const valid = Array.from(incoming).filter(f => f.type.startsWith('image/'));
    if (!valid.length) return;
    setPreviews(p => [...p, ...valid.map(f => URL.createObjectURL(f))]);
    setFiles(f => [...f, ...valid]);
    setScanError('');
  }

  function removeFile(i) {
    URL.revokeObjectURL(previews[i]);
    setFiles(f => f.filter((_, idx) => idx !== i));
    setPreviews(p => p.filter((_, idx) => idx !== i));
  }

  async function scan() {
    if (!files.length) return;
    setScanning(true); setScanError('');
    try {
      const blobs = await Promise.all(files.map(resizeImage));
      const formData = new FormData();
      blobs.forEach((blob, i) => formData.append('images', blob, files[i].name + '.jpg'));
      const res = await api.post('/api/v1/machines/extract', formData);
      onExtracted(res.data);
    } catch (e) {
      const msg = e.response?.data?.error
        || (!e.response ? 'Cannot reach server — restart the backend and try again' : `Error ${e.response.status}: ${e.response.statusText}`);
      setScanError(msg);
    }
    setScanning(false);
  }

  return (
    <div style={s.extractor}>
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '13px', fontWeight: '700', color: '#f0f2f5', marginBottom: '2px' }}>📷 Scan from photos</div>
        <div style={{ fontSize: '11px', color: '#6b7280' }}>Upload spec plates, datasheets or manuals — fields will be auto-filled</div>
      </div>
      <div style={s.dropZone}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current.click()}>
        <input ref={inputRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
          onChange={e => addFiles(e.target.files)} />
        <div style={{ fontSize: '22px', marginBottom: '6px' }}>⬆</div>
        <div style={{ fontSize: '12px', color: '#6b7280' }}>
          {files.length === 0 ? 'Drop images here or click to select'
            : `${files.length} image${files.length !== 1 ? 's' : ''} selected — add more or scan`}
        </div>
      </div>
      {previews.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
          {previews.map((url, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img src={url} alt="" style={s.previewImg} />
              <button style={s.removePreview} onClick={() => removeFile(i)}>×</button>
            </div>
          ))}
        </div>
      )}
      {scanError && (
        <div style={{ fontSize: '12px', color: '#fca5a5', marginTop: '8px', padding: '8px 10px', background: '#7f1d1d', borderRadius: '6px' }}>
          {scanError}
        </div>
      )}
      {files.length > 0 && (
        <button style={{ ...s.scanBtn, opacity: scanning ? 0.7 : 1, marginTop: '10px' }}
          disabled={scanning} onClick={scan}>
          {scanning ? '🔍 Analysing images…' : '🔍 Scan & fill form'}
        </button>
      )}
    </div>
  );
}

// ── MACHINE FORM ──────────────────────────────────────────────
function MachineForm({ machine, isDirector, onSaved, onCancel, onMaintenanceAffected }) {
  const { toast } = useToast();
  const isNew = !machine;
  const [form, setForm] = useState({
    // Identity
    name:                  machine?.name                  || '',
    display_name:          machine?.display_name          || '',
    maker:                 machine?.maker                 || '',
    model:                 machine?.model                 || '',
    serial_no:             machine?.serial_no             || '',
    site:                  machine?.site                  || '',
    year:                  machine?.year                  || '',
    // Clamping
    tonnes:                machine?.tonnes                || '',
    clamp_kn:              machine?.clamp_kn              || '',
    mould_min_mm:          machine?.mould_min_mm          || '',
    mould_max_mm:          machine?.mould_max_mm          || '',
    stroke_mm:             machine?.stroke_mm             || '',
    tie_bar_h_mm:          machine?.tie_bar_h_mm          || '',
    tie_bar_v_mm:          machine?.tie_bar_v_mm          || '',
    tie_bar_dia_mm:        machine?.tie_bar_dia_mm        || '',
    clamp_speed_mm_s:      machine?.clamp_speed_mm_s      || '',
    dry_cycle_s:           machine?.dry_cycle_s           || '',
    // Injection
    screw_mm:              machine?.screw_mm              || '',
    barrel_cm3:            machine?.barrel_cm3            || '',
    ld_ratio:              machine?.ld_ratio              || '',
    max_inj_bar:           machine?.max_inj_bar           || '',
    max_stg:               machine?.max_stg               || '',
    hld_stg:               machine?.hld_stg               || '',
    vp_type:               machine?.vp_type               || 'position',
    per_stg:               machine?.per_stg               || false,
    max_inj_speed_mm_s:    machine?.max_inj_speed_mm_s    || '',
    inj_stroke_mm:         machine?.inj_stroke_mm         || '',
    screw_type:            machine?.screw_type            || '',
    nozzle_type:           machine?.nozzle_type           || '',
    nozzle_radius_mm:      machine?.nozzle_radius_mm      || '',
    // Plasticising
    screw_spd_min:         machine?.screw_spd_min         || '',
    screw_spd_max:         machine?.screw_spd_max         || '',
    back_pres_min:         machine?.back_pres_min         || '',
    back_pres_max:         machine?.back_pres_max         || '',
    max_plast_rate_g_s:    machine?.max_plast_rate_g_s    || '',
    torque_nm:             machine?.torque_nm             || '',
    // Ejection
    ej_force_kn:           machine?.ej_force_kn           || '',
    ej_stroke_mm:          machine?.ej_stroke_mm          || '',
    ej_speed_mm_s:         machine?.ej_speed_mm_s         || '',
    ej_positions:          machine?.ej_positions          || '',
    ej_coupling:           machine?.ej_coupling           || '',
    air_valve_ports:       machine?.air_valve_ports       || '',
    // Movement
    plat_spd_mm_s:         machine?.plat_spd_mm_s         || '',
    mv_stg:                machine?.mv_stg                || '',
    // Machine Type & Drive
    drive_type:            machine?.drive_type            || '',
    clamp_type:            machine?.clamp_type            || '',
    injection_units:       machine?.injection_units       || 1,
    // Peripheral Equipment
    robot_type:            machine?.robot_type            || 'none',
    conveyor:              machine?.conveyor              || false,
    granulator:            machine?.granulator            || false,
    mould_temp_ports:      machine?.mould_temp_ports      || '',
    chiller_connected:     machine?.chiller_connected     || false,
    hot_runner_controller: machine?.hot_runner_controller || false,
    hot_runner_brand:      machine?.hot_runner_brand      || '',
    magnetic_clamping:          machine?.magnetic_clamping          || false,
    // Miscellaneous
    platen_type:                machine?.platen_type                || '',
    nozzle_contact_force_bar:   machine?.nozzle_contact_force_bar   || '',
    decomp_before_mm:           machine?.decomp_before_mm           || '',
    decomp_after_mm:            machine?.decomp_after_mm            || '',
    mould_breathing:            machine?.mould_breathing            || false,
    // Controller
    controller_brand:      machine?.controller_brand      || '',
    controller_model:      machine?.controller_model      || '',
    software_version:      machine?.software_version      || '',
    remote_access:         machine?.remote_access         || false,
    // Machine info
    kw:                    machine?.kw                    || '',
    status:                machine?.status                || 'available',
    // Financial
    rate_per_hr:           machine?.rate_per_hr           || '',
    labour_rate_per_hour:  machine?.labour_rate_per_hour  || '',
    maintenance_cost_year: machine?.maintenance_cost_year || '',
    electricity_rate:      machine?.electricity_rate      || '',
    // Safety
    last_service_date:     machine?.last_service_date     || '',
    next_service_date:     machine?.next_service_date     || '',
    ce_marked:             machine?.ce_marked             || false,
    // Cooling / Chiller
    chiller_capacity_kw:      machine?.chiller_capacity_kw      || '',
    chiller_temp_min_c:       machine?.chiller_temp_min_c       || '',
    cooling_flow_max_l_min:   machine?.cooling_flow_max_l_min   || '',
    cooling_pressure_max_bar: machine?.cooling_pressure_max_bar || '',
    coolant_dt_target_c:      machine?.coolant_dt_target_c      || '',
    ambient_temp_c:           machine?.ambient_temp_c           || '',
    ambient_rh_pct:           machine?.ambient_rh_pct           || '',
    // Maintenance
    maintenance_until:        machine?.maintenance_until        || '',
    maintenance_indefinite:   machine?.maintenance_indefinite   || false,
    // Scheduling
    warmup_min:            machine?.warmup_min            ?? '',
    // Notes
    machine_notes:         machine?.machine_notes         || '',
    notes:                 machine?.notes                 || '',
  });
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [highlighted, setHighlighted] = useState(new Set());

  function set(key, value) { setForm(f => ({ ...f, [key]: value })); }

  function handleExtracted(data) {
    const NUMERIC_KEYS = [
      'tonnes','clamp_kn','screw_mm','barrel_cm3','ld_ratio','max_inj_bar','max_stg',
      'hld_stg','plat_spd_mm_s','mv_stg','mould_min_mm','mould_max_mm','stroke_mm',
      'screw_spd_min','screw_spd_max','back_pres_min','back_pres_max','kw','year',
      'tie_bar_h_mm','tie_bar_v_mm','tie_bar_dia_mm','clamp_speed_mm_s','dry_cycle_s',
      'max_inj_speed_mm_s','inj_stroke_mm','nozzle_radius_mm',
      'max_plast_rate_g_s','torque_nm',
      'ej_force_kn','ej_stroke_mm','ej_speed_mm_s','ej_positions','air_valve_ports',
      'injection_units','mould_temp_ports',
      'nozzle_contact_force_bar','decomp_before_mm','decomp_after_mm',
    ];
    const filled = new Set();
    setForm(f => {
      const next = { ...f };
      Object.entries(data).forEach(([k, v]) => {
        if (v == null || !(k in next)) return;
        next[k] = NUMERIC_KEYS.includes(k) ? String(v) : v;
        filled.add(k);
      });
      return next;
    });
    setHighlighted(filled);
    setTimeout(() => setHighlighted(new Set()), 3500);
  }

  function save() {
    if (!form.name)     { setError('Machine name is required'); return; }
    if (!form.screw_mm) { setError('Screw diameter is required'); return; }
    setSaving(true);
    const payload = { ...form };

    [
      'tonnes','clamp_kn','screw_mm','barrel_cm3','ld_ratio','max_inj_bar','max_stg',
      'hld_stg','plat_spd_mm_s','mv_stg','mould_min_mm','mould_max_mm','stroke_mm',
      'screw_spd_min','screw_spd_max','back_pres_min','back_pres_max','kw','rate_per_hr','year',
      'tie_bar_h_mm','tie_bar_v_mm','tie_bar_dia_mm','clamp_speed_mm_s','dry_cycle_s',
      'max_inj_speed_mm_s','inj_stroke_mm','nozzle_radius_mm',
      'max_plast_rate_g_s','torque_nm',
      'ej_force_kn','ej_stroke_mm','ej_speed_mm_s','ej_positions','air_valve_ports',
      'injection_units','mould_temp_ports',
      'nozzle_contact_force_bar','decomp_before_mm','decomp_after_mm',
      'maintenance_cost_year','electricity_rate','labour_rate_per_hour',
      'chiller_capacity_kw','chiller_temp_min_c','cooling_flow_max_l_min','cooling_pressure_max_bar',
      'coolant_dt_target_c','ambient_temp_c','ambient_rh_pct',
      'warmup_min',
    ].forEach(k => { payload[k] = payload[k] !== '' ? parseFloat(payload[k]) : null; });

    ['last_service_date','next_service_date','maintenance_until'].forEach(k => {
      if (payload[k] === '') payload[k] = null;
    });

    const req = isNew
      ? api.post('/api/v1/machines', payload)
      : api.put(`/api/v1/machines/${machine.id}`, payload);

    req.then(res => {
      const result = res.data;
      const affectedJobs = result?.affected_jobs || [];
      const wentToMaintenance = !isNew && result?.machine?.status === 'maintenance' && machine?.status !== 'maintenance';
      if (wentToMaintenance) {
        if (affectedJobs.length > 0 && onMaintenanceAffected) {
          onMaintenanceAffected(result.machine, affectedJobs);
        } else {
          const until = result.machine.maintenance_indefinite ? 'indefinitely' : result.machine.maintenance_until ? `until ${result.machine.maintenance_until}` : '';
          toast.info(`${result.machine.name} set to maintenance ${until} — no active scheduled jobs affected. Scheduler will block this machine automatically.`);
        }
      }
      onSaved();
    }).catch(err => {
      setError(err.response?.data?.error || 'Save failed');
      setSaving(false);
    });
  }

  const hl = k => highlighted.has(k);

  return (
    <div style={s.page}>
      <div style={s.detailHeader}>
        <button style={s.backBtn} onClick={onCancel}>← Cancel</button>
        <h1 style={s.title}>{isNew ? 'Add Machine' : `Edit — ${machine.display_name || machine.name}`}</h1>
      </div>
      {!isNew && <LastChanged type="machine" id={machine.id} style={{ margin: '-6px 0 14px' }} />}

      <PhotoExtractor onExtracted={handleExtracted} />
      {error && <div style={s.formError}>{error}</div>}

      <div style={s.formGrid}>

        {/* 1. Identity */}
        <FormSection title="Identity">
          <Field label="Machine name *" value={form.name}         onChange={v => set('name', v)}         hl={hl('name')} />
          <Field label="Display name"   value={form.display_name} onChange={v => set('display_name', v)} placeholder='e.g. "Maria", "Line 3", "The Beast"' hint="If set, used everywhere in the app instead of the full name." />
          <Field label="Maker"          value={form.maker}        onChange={v => set('maker', v)}        hl={hl('maker')} />
          <Field label="Model"          value={form.model}        onChange={v => set('model', v)}        hl={hl('model')} />
          <Field label="Serial no."     value={form.serial_no}    onChange={v => set('serial_no', v)}    hl={hl('serial_no')} />
          <Field label="Site / Line"    value={form.site}         onChange={v => set('site', v)} />
          <Field label="Year"           value={form.year}         onChange={v => set('year', v)}         hl={hl('year')} type="number" />
        </FormSection>

        {/* 2. Clamping */}
        <FormSection title="Clamping">
          <Field label="Clamp force (T) *"      value={form.tonnes}           onChange={v => set('tonnes', v)}           hl={hl('tonnes')}           type="number" />
          <Field label="Clamp force (kN)"       value={form.clamp_kn}         onChange={v => set('clamp_kn', v)}         hl={hl('clamp_kn')}         type="number" />
          <Field label="Mould height min (mm)"  value={form.mould_min_mm}     onChange={v => set('mould_min_mm', v)}     hl={hl('mould_min_mm')}     type="number" />
          <Field label="Mould height max (mm)"  value={form.mould_max_mm}     onChange={v => set('mould_max_mm', v)}     hl={hl('mould_max_mm')}     type="number" />
          <Field label="Stroke (mm)"            value={form.stroke_mm}        onChange={v => set('stroke_mm', v)}        hl={hl('stroke_mm')}        type="number" />
          <Field label="Tie bar spacing H (mm)" value={form.tie_bar_h_mm}     onChange={v => set('tie_bar_h_mm', v)}     hl={hl('tie_bar_h_mm')}     type="number" />
          <Field label="Tie bar spacing V (mm)" value={form.tie_bar_v_mm}     onChange={v => set('tie_bar_v_mm', v)}     hl={hl('tie_bar_v_mm')}     type="number" />
          <Field label="Tie bar diameter (mm)"  value={form.tie_bar_dia_mm}   onChange={v => set('tie_bar_dia_mm', v)}   hl={hl('tie_bar_dia_mm')}   type="number" />
          <Field label="Clamping speed (mm/s)"  value={form.clamp_speed_mm_s} onChange={v => set('clamp_speed_mm_s', v)} hl={hl('clamp_speed_mm_s')} type="number" />
          <Field label="Dry cycle time (s)"     value={form.dry_cycle_s}      onChange={v => set('dry_cycle_s', v)}      hl={hl('dry_cycle_s')}      type="number" />
          <SelectField label="Clamping type" value={form.clamp_type} onChange={v => set('clamp_type', v)}
            options={[{v:'',l:'— select —'},{v:'toggle',l:'Toggle'},{v:'direct-hydraulic',l:'Direct hydraulic'},{v:'electric',l:'Electric'}]} />
        </FormSection>

        {/* 3. Injection */}
        <FormSection title="Injection">
          <Field label="Screw diameter (mm) *"        value={form.screw_mm}          onChange={v => set('screw_mm', v)}          hl={hl('screw_mm')}          type="number" />
          <Field label="Barrel volume (cm³)"          value={form.barrel_cm3}        onChange={v => set('barrel_cm3', v)}        hl={hl('barrel_cm3')}        type="number" />
          <Field label="L/D ratio"                    value={form.ld_ratio}          onChange={v => set('ld_ratio', v)}          hl={hl('ld_ratio')}          type="number" />
          <Field label="Max injection pressure (bar)" value={form.max_inj_bar}       onChange={v => set('max_inj_bar', v)}       hl={hl('max_inj_bar')}       type="number" />
          <Field label="Max injection stages"         value={form.max_stg}           onChange={v => set('max_stg', v)}           hl={hl('max_stg')}           type="number" />
          <Field label="Hold pressure stages"         value={form.hld_stg}           onChange={v => set('hld_stg', v)}           hl={hl('hld_stg')}           type="number" />
          <Field label="Max injection speed (mm/s)"   value={form.max_inj_speed_mm_s} onChange={v => set('max_inj_speed_mm_s', v)} hl={hl('max_inj_speed_mm_s')} type="number" />
          <Field label="Injection stroke (mm)"        value={form.inj_stroke_mm}     onChange={v => set('inj_stroke_mm', v)}     hl={hl('inj_stroke_mm')}     type="number" />
          <Field label="Nozzle radius (mm)"           value={form.nozzle_radius_mm}  onChange={v => set('nozzle_radius_mm', v)}  hl={hl('nozzle_radius_mm')}  type="number" />
          <SelectField label="Screw type" value={form.screw_type} onChange={v => set('screw_type', v)}
            options={[{v:'',l:'— select —'},{v:'general',l:'General purpose'},{v:'barrier',l:'Barrier'},{v:'mixing',l:'Mixing'},{v:'high-shear',l:'High shear'}]} />
          <SelectField label="Nozzle type" value={form.nozzle_type} onChange={v => set('nozzle_type', v)}
            options={[{v:'',l:'— select —'},{v:'open',l:'Open'},{v:'shut-off',l:'Shut-off'},{v:'hot-tip',l:'Hot tip'}]} />
          <SelectField label="V→P switchover" value={form.vp_type} onChange={v => set('vp_type', v)}
            options={[{v:'position',l:'Position'},{v:'pressure',l:'Pressure'},{v:'time',l:'Time'}]} />
          <CheckField label="Per-stage pressure control" checked={form.per_stg} onChange={v => set('per_stg', v)} />
        </FormSection>

        {/* 4. Plasticising */}
        <FormSection title="Plasticising">
          <Field label="Screw speed min (rpm)"    value={form.screw_spd_min}     onChange={v => set('screw_spd_min', v)}     hl={hl('screw_spd_min')}     type="number" />
          <Field label="Screw speed max (rpm)"    value={form.screw_spd_max}     onChange={v => set('screw_spd_max', v)}     hl={hl('screw_spd_max')}     type="number" />
          <Field label="Back pressure min (bar)"  value={form.back_pres_min}     onChange={v => set('back_pres_min', v)}     hl={hl('back_pres_min')}     type="number" />
          <Field label="Back pressure max (bar)"  value={form.back_pres_max}     onChange={v => set('back_pres_max', v)}     hl={hl('back_pres_max')}     type="number" />
          <Field label="Max plast. rate (g/s)"    value={form.max_plast_rate_g_s} onChange={v => set('max_plast_rate_g_s', v)} hl={hl('max_plast_rate_g_s')} type="number" />
          <Field label="Screw torque (N·m)"       value={form.torque_nm}         onChange={v => set('torque_nm', v)}         hl={hl('torque_nm')}         type="number" />
        </FormSection>

        {/* 5. Ejection */}
        <FormSection title="Ejection">
          <Field label="Ejector force (kN)"   value={form.ej_force_kn}    onChange={v => set('ej_force_kn', v)}    hl={hl('ej_force_kn')}    type="number" />
          <Field label="Ejector stroke (mm)"  value={form.ej_stroke_mm}   onChange={v => set('ej_stroke_mm', v)}   hl={hl('ej_stroke_mm')}   type="number" />
          <Field label="Ejector speed (mm/s)" value={form.ej_speed_mm_s}  onChange={v => set('ej_speed_mm_s', v)}  hl={hl('ej_speed_mm_s')}  type="number" />
          <Field label="Ejector positions"    value={form.ej_positions}   onChange={v => set('ej_positions', v)}   hl={hl('ej_positions')}   type="number" />
          <Field label="Air valve ports"      value={form.air_valve_ports} onChange={v => set('air_valve_ports', v)} hl={hl('air_valve_ports')} type="number" />
          <SelectField label="Coupling type" value={form.ej_coupling} onChange={v => set('ej_coupling', v)}
            options={[{v:'',l:'— select —'},{v:'hydraulic',l:'Hydraulic'},{v:'mechanical',l:'Mechanical'},{v:'electric',l:'Electric'}]} />
        </FormSection>

        {/* 6. Movement */}
        <FormSection title="Movement">
          <Field label="Platen speed (mm/s)" value={form.plat_spd_mm_s} onChange={v => set('plat_spd_mm_s', v)} hl={hl('plat_spd_mm_s')} type="number" />
          <Field label="Move stages"         value={form.mv_stg}        onChange={v => set('mv_stg', v)}        hl={hl('mv_stg')}        type="number" />
        </FormSection>

        {/* 7. Miscellaneous */}
        <FormSection title="Miscellaneous">
          <SelectField label="Machine type" value={form.drive_type} onChange={v => set('drive_type', v)}
            options={[{v:'',l:'— select —'},{v:'hydraulic',l:'Hydraulic'},{v:'servo-hydraulic',l:'Servo-hydraulic'},{v:'all-electric',l:'All-electric'},{v:'hybrid',l:'Hybrid'}]} />
          <Field label="Injection units" value={form.injection_units} onChange={v => set('injection_units', v)} hl={hl('injection_units')} type="number" />
          <SelectField label="Platen type" value={form.platen_type} onChange={v => set('platen_type', v)}
            options={[{v:'',l:'— select —'},{v:'standard',l:'Standard'},{v:'rotary',l:'Rotary'},{v:'cube',l:'Cube'}]} />
          <SelectField label="Robot / automation" value={form.robot_type} onChange={v => set('robot_type', v)}
            options={[{v:'none',l:'None'},{v:'sprue-picker',l:'Sprue picker'},{v:'full-robot',l:'Full robot'}]} />
          <Field label="Nozzle contact force (bar)"       value={form.nozzle_contact_force_bar} onChange={v => set('nozzle_contact_force_bar', v)} hl={hl('nozzle_contact_force_bar')} type="number" />
          <Field label="Decompression before dosing (mm)" value={form.decomp_before_mm}         onChange={v => set('decomp_before_mm', v)}         hl={hl('decomp_before_mm')}         type="number" />
          <Field label="Decompression after dosing (mm)"  value={form.decomp_after_mm}          onChange={v => set('decomp_after_mm', v)}          hl={hl('decomp_after_mm')}          type="number" />
          <CheckField label="Mould breathing capable"    checked={form.mould_breathing}   onChange={v => set('mould_breathing', v)} />
          <CheckField label="Magnetic mould clamping"    checked={form.magnetic_clamping} onChange={v => set('magnetic_clamping', v)} />
        </FormSection>

        {/* 8. Peripheral Equipment */}
        <FormSection title="Peripheral Equipment">
          <Field label="Mould temp controller ports" value={form.mould_temp_ports} onChange={v => set('mould_temp_ports', v)} type="number" />
          <CheckField label="Conveyor"               checked={form.conveyor}              onChange={v => set('conveyor', v)} />
          <CheckField label="Granulator"             checked={form.granulator}            onChange={v => set('granulator', v)} />
          <CheckField label="Chiller connected"      checked={form.chiller_connected}     onChange={v => set('chiller_connected', v)} />
          <CheckField label="Hot runner controller"  checked={form.hot_runner_controller} onChange={v => set('hot_runner_controller', v)} />
          {form.hot_runner_controller && (
            <Field label="Hot runner brand" value={form.hot_runner_brand} onChange={v => set('hot_runner_brand', v)} />
          )}
        </FormSection>

        {/* 9. Controller */}
        <FormSection title="Controller">
          <Field label="Brand"            value={form.controller_brand} onChange={v => set('controller_brand', v)} />
          <Field label="Model"            value={form.controller_model} onChange={v => set('controller_model', v)} />
          <Field label="Software version" value={form.software_version} onChange={v => set('software_version', v)} />
          <CheckField label="Remote access" checked={form.remote_access} onChange={v => set('remote_access', v)} />
        </FormSection>

        {/* 10. Financial — director only */}
        {isDirector && (
          <FormSection title="Financial">
            <Field label="Rate per hour (€/hr)"       value={form.rate_per_hr}           onChange={v => set('rate_per_hr', v)}           type="number" />
            <Field label="Labour rate per hour (€/hr)" value={form.labour_rate_per_hour} onChange={v => set('labour_rate_per_hour', v)} type="number" />
            <Field label="Maintenance cost/year (€)"  value={form.maintenance_cost_year}  onChange={v => set('maintenance_cost_year', v)}  type="number" />
            <Field label="Electricity rate (€/kWh)"   value={form.electricity_rate}       onChange={v => set('electricity_rate', v)}       type="number" />
          </FormSection>
        )}

        {/* 11. Safety & Compliance */}
        <FormSection title="Safety & Compliance">
          <Field label="Last service date" value={form.last_service_date} onChange={v => set('last_service_date', v)} type="date" />
          <Field label="Next service due"  value={form.next_service_date} onChange={v => set('next_service_date', v)} type="date" />
          <CheckField label="CE marked"   checked={form.ce_marked}       onChange={v => set('ce_marked', v)} />
        </FormSection>

        {/* 12. Machine info */}
        <FormSection title="Machine info">
          <Field label="Power (kW)" value={form.kw}     onChange={v => set('kw', v)}     hl={hl('kw')} type="number" />
          <SelectField label="Status" value={form.status} onChange={v => set('status', v)}
            options={[{v:'available',l:'Available'},{v:'in-use',l:'In use'},{v:'maintenance',l:'Maintenance'},{v:'archived',l:'Decommissioned'}]} />
          {form.status === 'maintenance' && (
            <div style={{ background: '#1a1f2e', border: '1px solid #92400e', borderRadius: '8px', padding: '12px', marginTop: '4px' }}>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#f59e0b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Maintenance duration</div>
              <CheckField label="Indefinite (until manually changed)" checked={form.maintenance_indefinite} onChange={v => set('maintenance_indefinite', v)} />
              {!form.maintenance_indefinite && (
                <Field label="Expected back (date)" value={form.maintenance_until} onChange={v => set('maintenance_until', v)} type="date" />
              )}
            </div>
          )}
        </FormSection>

        {/* 13. Cooling / Chiller */}
        <FormSection title="Cooling / Chiller">
          <Field label="Chiller cooling capacity (kW)"  value={form.chiller_capacity_kw}      onChange={v => set('chiller_capacity_kw', v)}      type="number" />
          <Field label="Min coolant temperature (°C)"   value={form.chiller_temp_min_c}        onChange={v => set('chiller_temp_min_c', v)}        type="number" />
          <Field label="Max flow rate (l/min)"          value={form.cooling_flow_max_l_min}    onChange={v => set('cooling_flow_max_l_min', v)}    type="number" />
          <Field label="Max supply pressure (bar)"      value={form.cooling_pressure_max_bar}  onChange={v => set('cooling_pressure_max_bar', v)}  type="number" />
          <Field label="Target coolant ΔT (°C)"         value={form.coolant_dt_target_c}       onChange={v => set('coolant_dt_target_c', v)}       type="number" />
          <Field label="Ambient temperature (°C)"       value={form.ambient_temp_c}            onChange={v => set('ambient_temp_c', v)}            type="number" />
          <Field label="Ambient humidity (%RH)"         value={form.ambient_rh_pct}            onChange={v => set('ambient_rh_pct', v)}            type="number" />
        </FormSection>

        {/* 14. Scheduling */}
        <FormSection title="Scheduling">
          <Field label="Machine warmup time (min)" value={form.warmup_min} onChange={v => set('warmup_min', v)} type="number" />
          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '-4px', marginBottom: '6px' }}>
            Time lost each morning before stable production (default 30 min if not set). Shift 2 is always full — machine stays warm.
          </div>
        </FormSection>

        {/* 15. Machine Notes */}
        <FormSection title="Machine Notes (shown prominently on detail page)">
          <Field
            label="Known issues, quirks, or important reminders for setters and operators"
            value={form.machine_notes}
            onChange={v => set('machine_notes', v)}
            multiline
          />
        </FormSection>

        {/* 16. General Notes */}
        <FormSection title="General Notes">
          <Field label="Internal notes" value={form.notes} onChange={v => set('notes', v)} multiline />
        </FormSection>

      </div>

      <div style={s.formFooter}>
        <button style={s.backBtn} onClick={onCancel}>Cancel</button>
        <button style={{ ...s.saveBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
          {saving ? 'Saving...' : isNew ? 'Add Machine' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ── MACHINE MAINTENANCE MODAL ─────────────────────────────────
function MachineMaintenanceModal({ machine, affectedJobs, onDone }) {
  const { toast } = useToast();
  const [decisions, setDecisions] = useState(() =>
    Object.fromEntries(affectedJobs.map(j => [j.plan_id, { action: 'later', machine_id: '' }]))
  );
  const [submitting, setSubmitting] = useState(false);

  function setDecision(planId, key, value) {
    setDecisions(d => ({ ...d, [planId]: { ...d[planId], [key]: value } }));
  }

  async function submit() {
    setSubmitting(true);
    const decisionList = Object.entries(decisions).map(([plan_id, d]) => ({ plan_id, ...d }));
    try {
      await api.post(`/api/v1/machines/${machine.id}/resolve-maintenance`, { decisions: decisionList });
      toast.success('Decisions saved');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save decisions');
      setSubmitting(false);
    }
  }

  const untilLabel = machine.maintenance_indefinite
    ? 'indefinitely'
    : machine.maintenance_until ? `until ${machine.maintenance_until}` : '';

  return (
    <div style={mm.overlay}>
      <div style={mm.panel}>
        <div style={mm.header}>
          <div>
            <div style={mm.title}>{machine.display_name || machine.name} — maintenance {untilLabel}</div>
            <div style={mm.sub}>{affectedJobs.length} upcoming production run{affectedJobs.length !== 1 ? 's' : ''} affected</div>
          </div>
        </div>

        <div style={mm.body}>
          {affectedJobs.map(job => {
            const dec = decisions[job.plan_id] || {};
            return (
              <div key={job.plan_id} style={mm.jobCard}>
                <div style={mm.jobHeader}>
                  <span style={mm.jobPart}>{job.part_name}</span>
                  {job.part_number && <span style={mm.jobPN}>{job.part_number}</span>}
                  <span style={mm.jobQty}>{job.total_qty.toLocaleString()} pcs</span>
                </div>
                <div style={mm.jobMeta}>
                  {job.order_customer && <span>{job.order_customer}</span>}
                  {job.delivery_week && <span>W{job.delivery_week}/{job.delivery_year}</span>}
                  <span>{job.scheduled_dates.length} day{job.scheduled_dates.length !== 1 ? 's' : ''} scheduled</span>
                </div>

                <div style={mm.options}>
                  <label style={mm.optLabel}>
                    <input type="radio" name={job.plan_id} value="later"
                      checked={dec.action === 'later'}
                      onChange={() => setDecision(job.plan_id, 'action', 'later')} />
                    <span>Reassign later <span style={mm.optNote}>(creates notification)</span></span>
                  </label>

                  {job.push_feasible && (
                    <label style={mm.optLabel}>
                      <input type="radio" name={job.plan_id} value="push"
                        checked={dec.action === 'push'}
                        onChange={() => setDecision(job.plan_id, 'action', 'push')} />
                      <span>Push to after maintenance <span style={mm.optNote}>({machine.maintenance_until})</span></span>
                    </label>
                  )}

                  {job.secondary_machines.length > 0 && (
                    <label style={mm.optLabel}>
                      <input type="radio" name={job.plan_id} value="reassign"
                        checked={dec.action === 'reassign'}
                        onChange={() => setDecision(job.plan_id, 'action', 'reassign')} />
                      <span>Reassign to secondary machine</span>
                    </label>
                  )}

                  {dec.action === 'reassign' && job.secondary_machines.length > 0 && (
                    <select value={dec.machine_id} onChange={e => setDecision(job.plan_id, 'machine_id', e.target.value)}
                      style={{ ...s.formInput, marginTop: '6px', marginLeft: '20px', width: 'calc(100% - 20px)' }}>
                      <option value="">— select machine —</option>
                      {job.secondary_machines.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div style={mm.footer}>
          <button style={mm.cancelBtn} onClick={onDone} disabled={submitting}>Dismiss</button>
          <button style={{ ...mm.submitBtn, opacity: submitting ? 0.6 : 1 }} onClick={submit} disabled={submitting}>
            {submitting ? 'Saving...' : 'Apply decisions'}
          </button>
        </div>
      </div>
    </div>
  );
}

const mm = {
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' },
  panel:     { background: '#1e2329', border: '1px solid #92400e', borderRadius: '14px', width: '100%', maxWidth: '620px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header:    { padding: '20px 24px 16px', borderBottom: '1px solid #2a3040' },
  title:     { fontSize: '16px', fontWeight: '700', color: '#f59e0b', marginBottom: '4px' },
  sub:       { fontSize: '12px', color: '#6b7280' },
  body:      { padding: '16px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' },
  jobCard:   { background: '#0f1117', border: '1px solid #2a3040', borderRadius: '10px', padding: '14px' },
  jobHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' },
  jobPart:   { fontSize: '13px', fontWeight: '700', color: '#f0f2f5' },
  jobPN:     { fontSize: '11px', color: '#6b7280', fontFamily: 'monospace' },
  jobQty:    { marginLeft: 'auto', fontSize: '11px', fontWeight: '600', color: '#14b8a6' },
  jobMeta:   { display: 'flex', gap: '10px', fontSize: '11px', color: '#6b7280', marginBottom: '10px' },
  options:   { display: 'flex', flexDirection: 'column', gap: '6px' },
  optLabel:  { display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12px', color: '#d1d5db', cursor: 'pointer' },
  optNote:   { color: '#6b7280', fontSize: '11px' },
  footer:    { padding: '16px 24px', borderTop: '1px solid #2a3040', display: 'flex', justifyContent: 'space-between', gap: '10px' },
  cancelBtn: { background: 'none', border: '1px solid #2a3040', color: '#6b7280', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', cursor: 'pointer' },
  submitBtn: { background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
};

// ── SHARED COMPONENTS ─────────────────────────────────────────
function Spec({ label, value }) {
  return (
    <div style={s.spec}>
      <span style={s.specLabel}>{label}</span>
      <span style={s.specValue}>{filled(value)}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={s.row}>
      <span style={s.rowLabel}>{label}</span>
      <span style={s.rowValue}>{filled(value)}</span>
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <div style={s.formSection}>
      <div style={s.formSectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', multiline, hl, placeholder, hint }) {
  const inputStyle = { ...s.formInput, ...(hl ? s.hlInput : {}) };
  return (
    <div style={s.formField}>
      <label style={s.formLabel}>{label}</label>
      {multiline
        ? <textarea value={value} onChange={e => onChange(e.target.value)} style={s.formTextarea} rows={3} placeholder={placeholder} />
        : <input type={type} value={value} onChange={e => onChange(e.target.value)} style={inputStyle} placeholder={placeholder} />
      }
      {hint && <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '3px', lineHeight: 1.4 }}>{hint}</div>}
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div style={s.formField}>
      <label style={s.formLabel}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} style={s.formInput}>
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

function CheckField({ label, checked, onChange }) {
  return (
    <div style={{ ...s.formField, flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ width: '16px', height: '16px' }} />
      <label style={{ ...s.formLabel, margin: 0 }}>{label}</label>
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────────
const s = {
  page:         cs.page,
  header:       cs.pageHeader,
  title:        cs.pageTitle,
  sub:          cs.pageSub,
  addBtn:       cs.btnPrimary,
  importBtn:    cs.btnSecondary,
  grid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' },
  card:         { background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden', cursor: 'pointer' },
  cardHeader:   { padding: '16px 18px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${C.border}` },
  machineName:  { fontSize: '15px', fontWeight: '700' },
  warmupFlag:   { fontSize: '9px', fontWeight: '700', letterSpacing: '0.03em', padding: '2px 6px',
                  borderRadius: '4px', whiteSpace: 'nowrap', cursor: 'help',
                  background: 'rgba(245,158,11,0.12)', color: '#f59e0b',
                  border: '1px solid rgba(245,158,11,0.35)' },
  statusDot:    { width: '8px', height: '8px', borderRadius: '50%' },
  cardBody:     { padding: '14px 18px' },
  site:         { fontSize: '11px', color: C.muted, marginBottom: '12px' },
  specs:        { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  spec:         { display: 'flex', flexDirection: 'column', gap: '2px' },
  specLabel:    { fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: C.muted },
  specValue:    { fontSize: '13px', fontWeight: '600', fontFamily: 'monospace', color: C.text },
  cardFooter:   { padding: '10px 18px', borderTop: `1px solid ${C.border}` },
  statusChip:   { fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '10px' },
  banner:       { width: '100%', height: '200px', borderRadius: '12px', marginBottom: '20px', position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', padding: '12px', boxSizing: 'border-box', overflow: 'hidden', background: C.surface },
  bannerImg:    { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' },
  bannerPlaceholder: { color: C.mutedDark, fontSize: '13px' },
  uploadBtn:    { background: 'rgba(0,0,0,0.6)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', backdropFilter: 'blur(4px)', zIndex: 1 },
  thumbnail:    { width: '56px', height: '56px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 },
  titleRow:     { display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  detailActions:{ display: 'flex', gap: '8px' },
  detailTitle:  { fontSize: '26px', fontWeight: '700', marginBottom: '4px' },
  detailSub:    { fontSize: '13px', color: C.muted },
  notesBox:     { background: '#1e3a5f', border: '1px solid #1d4ed8', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px' },
  notesTitle:   { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#93c5fd', marginBottom: '6px' },
  notesText:    { fontSize: '13px', color: '#bfdbfe', lineHeight: '1.6' },
  detailGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' },
  section:      cs.card,
  sectionTitle: { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: C.teal, marginBottom: '12px' },
  row:          { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.border}` },
  rowLabel:     { fontSize: '12px', color: C.muted },
  rowValue:     { fontSize: '12px', fontWeight: '600', fontFamily: 'monospace' },
  backBtn:      cs.btnSecondary,
  editBtn:      { background: '#1d4ed8', color: '#bfdbfe', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  deleteBtn:    { background: C.dangerBg, color: C.dangerText, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  formGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px', marginBottom: '24px' },
  formSection:  cs.card,
  formSectionTitle: { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: C.teal, marginBottom: '12px' },
  formField:    { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' },
  formLabel:    cs.label,
  formInput:    cs.input,
  formTextarea: { ...cs.input, resize: 'vertical' },
  formFooter:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px' },
  formError:    cs.errorBanner,
  saveBtn:      { ...cs.btnPrimary, padding: '10px 24px' },
  center:       { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'system-ui, sans-serif', color: C.muted },
  extractor:    { ...cs.card, borderRadius: '12px', marginBottom: '20px' },
  dropZone:     { border: `2px dashed ${C.border}`, borderRadius: '8px', padding: '20px 16px', textAlign: 'center', cursor: 'pointer' },
  previewImg:   { width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: `1px solid ${C.border}`, display: 'block' },
  removePreview:{ position: 'absolute', top: '-6px', right: '-6px', background: C.danger, border: 'none', color: '#fff', borderRadius: '50%', width: '18px', height: '18px', cursor: 'pointer', fontSize: '13px', lineHeight: '18px', textAlign: 'center', padding: 0 },
  scanBtn:      { width: '100%', background: '#1d4ed8', color: '#bfdbfe', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  hlInput:      { borderColor: C.teal, boxShadow: '0 0 0 2px rgba(20,184,166,0.25)', transition: 'box-shadow 0.3s, border-color 0.3s' },
};
